import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { ModalRegister, Modal } from "../../../../components/ui/ModalRegister";
import PdgOS from "../../../origami/components/ui/PdgOS";
import ServerTerminal from "../../../origami/components/puzzles/ServerTerminal";
import DoorPad from "../../../origami/components/puzzles/DoorPad";
import MonitorDashboard from "../../../origami/components/puzzles/MonitorDashboard";
import { useInspections } from "../../../../hooks/useInspections";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import { useHints } from "../../../../hooks/useHints";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import HintsModalHub from "../../../../components/ui/HintsModalHub";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import CutsceneSlideshow from "../../../../components/ui/CutsceneSlideshow";

function HintButton({ onHint }) {
  return (
    <button type="button" onClick={onHint} style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", cursor: "pointer", borderRadius: 4,
      border: "1px solid rgba(251,191,36,.4)", background: "rgba(251,191,36,.1)",
      color: "rgba(251,191,36,.9)", fontSize: 10, fontWeight: 800,
      letterSpacing: ".1em", textTransform: "uppercase",
      fontFamily: "ui-monospace,'SF Mono',Menlo,monospace",
    }}>◈ Indices</button>
  );
}

export default function Stage3SyncPanel({
  code, playerId, myVariant, playerCount, apiBaseUrl, onAllReady,
  centerMaxH, pseudo, canAdvance, isCanAdvance,
  raisedCount = 0, raisedPlayers = [], hintSignal = 0,
  onSuccessCutsceneStart, onSuccessCutsceneEnd, onNavigateToScoreboard,
}) {
  const hasUnlockedOfficeRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [allReady, setAllReady] = useState(false);
  const [serverPlayerCount, setServerPlayerCount] = useState(null);
  const [selectionByVariant, setSelectionByVariant] = useState({});
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showPdgModal, setShowPdgModal] = useState(false);
  const [unlockPhase, setUnlockPhase] = useState("idle");
  const socketRef = useRef(null);
  const [showServerModal, setShowServerModal] = useState(false);
  const [serverState, setServerState] = useState(null);
  const [serverComplete, setServerComplete] = useState(false);
  const [usbEjected, setUsbEjected] = useState(false);
  const [voteState, setVoteState] = useState(null);
  const [showDoorModal, setShowDoorModal] = useState(false);
  const [doorState, setDoorState] = useState(null);
  const [doorComplete, setDoorComplete] = useState(false);
  const [showCutscene, setShowCutscene] = useState(false);
  const [showMonitorModal, setShowMonitorModal] = useState(false);
  const [monitorState, setMonitorState] = useState(null);
  const [padUnlocked, setPadUnlocked] = useState(false);
  const [pseudoByVariant, setPseudoByVariant] = useState({});
  const [showHintModal, setShowHintModal] = useState(false);

  const onAllReadyRef = useRef(onAllReady);
  useEffect(() => { onAllReadyRef.current = onAllReady; }, [onAllReady]);

  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 3, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 3 });
  const { unlockScenarioStep } = useScenarioTracker({ code, stage: 3 });

  useEffect(() => {
    if (hasUnlockedOfficeRef.current) return;
    hasUnlockedOfficeRef.current = true;
    unlockScenarioStep("s3.ceo.office");
  }, [unlockScenarioStep]);

  const hasAutoOpenedUnlockedRef = useRef(false);
  const socketUrl = useMemo(() => apiBaseUrl || process.env.REACT_APP_API_URL || "http://localhost:4000", [apiBaseUrl]);
  const N = serverPlayerCount || playerCount || 6;
  const effectiveMyVariant = Number(myVariant);

  const stage3Config = useMemo(() => {
    const n = [4, 5, 6].includes(Number(N)) ? Number(N) : 6;
    const syncSolution = { 4: { 1: 2, 2: 1, 3: 4, 4: 3 }, 5: { 1: 2, 2: 1, 3: 4, 4: 3, 5: 2 }, 6: { 1: 2, 2: 1, 3: 4, 4: 3, 5: 2, 6: 4 } }[n];
    const proofAnswers = {
      4: [{ id: 1, path: "Ce PC/Bureau" }, { id: 2, path: "Ce PC/Documents/Confidentiel" }, { id: 3, path: "Ce PC/Documents/Confidentiel/Finances" }, { id: 4, path: "Ce PC/Documents/Confidentiel/RH" }],
      5: [{ id: 1, path: "Ce PC/Bureau" }, { id: 2, path: "Ce PC/Documents/Confidentiel" }, { id: 3, path: "Ce PC/Documents/Confidentiel/Finances" }, { id: 4, path: "Ce PC/Documents/Confidentiel/RH" }, { id: 5, path: "Ce PC/Documents/Notes Personnelles" }],
      6: [{ id: 1, path: "Ce PC/Bureau" }, { id: 2, path: "Ce PC/Documents/Confidentiel" }, { id: 3, path: "Ce PC/Documents/Confidentiel/Finances" }, { id: 4, path: "Ce PC/Documents/Confidentiel/RH" }, { id: 5, path: "Ce PC/Documents/Notes Personnelles" }, { id: 6, path: "Ce PC/Documents/Rapports" }],
    }[n];
    return { playerCount: n, syncSolution, proofAnswers };
  }, [N]);

  const SOLUTION_BY_VARIANT = stage3Config.syncSolution;
  const proofAnswers = stage3Config.proofAnswers;

  const activeSelectionEntries = useMemo(() =>
    Object.entries(selectionByVariant || {}).filter(([v]) => { const n = Number(v); return Number.isFinite(n) && n >= 1 && n <= N; }),
    [selectionByVariant, N]
  );

  const syncAnswerLines = useMemo(() =>
    Object.entries(SOLUTION_BY_VARIANT).map(([variant, switchId]) => ({ variant: Number(variant), switchId })),
    [SOLUTION_BY_VARIANT]
  );

  const INSPECT_KEYS = useMemo(() =>
    getInspectKeys({ gameId: 1, playerCount: playerCount || 6, stage: 3, seat: effectiveMyVariant }),
    [playerCount, effectiveMyVariant]
  );

  const { inspectedCount, total } = useInspectProgress({ sessionCode: code, stage: 3, playerId: Number(playerId), keys: INSPECT_KEYS });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 3, playerId: Number(playerId), metricsPlayerId: 0 });

  function getColorForVariant(v) {
    const colors = ['rgb(34,211,238)', 'rgb(217,70,239)', 'rgb(251,191,36)', 'rgb(52,211,153)', 'rgb(129,140,248)', 'rgb(249,115,22)'];
    return colors[(v - 1) % colors.length];
  }

  const zones = useMemo(() => {
    return Array.from({ length: N }, (_, i) => {
      const v = i + 1;
      return { variant: v, pseudo: pseudoByVariant[String(v)] || `J${v}`, pads: [1, 2, 3, 4], color: getColorForVariant(v) };
    });
  }, [N, pseudoByVariant]);

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socketUrl) return;
    const s = io(socketUrl, { transports: ["websocket"], withCredentials: true, autoConnect: true, reconnection: true, reconnectionAttempts: Infinity, reconnectionDelayMax: 2000 });
    socketRef.current = s;

    const joinIfReady = () => {
      const cleanCode = String(code || "").toUpperCase().trim();
      const pid = Number(playerId);
      if (!cleanCode || !Number.isFinite(pid)) return;
      s.emit("session:join", { code: cleanCode, playerId: pid, pseudo });
    };

    s.on("connect", () => { setConnected(true); joinIfReady(); });
    s.on("disconnect", () => setConnected(false));
    s.on("stage3:sync", (payload) => {
      if (payload?.selectionByVariant) setSelectionByVariant(payload.selectionByVariant);
      if (typeof payload?.allReady === "boolean") setAllReady(payload.allReady);
      if (payload?.pseudoByVariant) setPseudoByVariant(payload.pseudoByVariant);
      if (Number.isFinite(Number(payload?.playerCount))) setServerPlayerCount(Number(payload.playerCount));
    });
    s.on("stage3:allReady", () => setAllReady(true));
    s.on("stage3:server:sync", (payload) => {
      setServerState(payload);
      if (payload?.allUploaded) { missionResolve({ key: "s3.terminal.found" }); setServerComplete(true); }
    });
    s.on("stage3:server:complete", () => {
      setServerComplete(true);
      missionUnlock({ key: "s3.evidence.uploaded", text: "Toutes les preuves ont été transférées sur la clé USB." });
    });
    s.on("stage3:copy:ejected", () => {
      missionResolve({ key: "s3.computer.unlocked" });
      unlockScenarioStep("s3.evidence.found");
      setUsbEjected(true);
    });
    s.on("stage3:server:vote", (payload) => setVoteState(payload));
    s.on("stage3:server:vote:success", () => setVoteState(null));
    s.on("stage3:server:vote:expired", () => setVoteState(null));
    s.on("stage3:door:sync", (payload) => { setDoorState(payload); if (payload?.unlocked) setDoorComplete(true); });
    s.on("stage3:door:complete", () => {
      setDoorComplete(true);
      // NE PAS fermer la modale ici — DoorPad doit rester monté pour jouer l'animation
      missionUnlock({ key: "s3.door.opened", text: "Porte de la salle de conférence déverrouillée." });
      onSuccessCutsceneStart?.();
    });
    s.on("stage3:monitor:sync", (payload) => {
      setMonitorState(payload);
      if (payload?.unlocked && !padUnlocked) {
        setPadUnlocked(true);
        missionUnlock({ key: "s3.monitor.complete", text: "Pad biométrique réactivé — accès porte disponible." });
      }
    });

    joinIfReady();
    return () => { s.disconnect(); socketRef.current = null; };
  }, [code, playerId, socketUrl, pseudo]);

  // ── Restauration au refresh ─────────────────────────────────────────────────
  useEffect(() => {
    if (!code) return;
    const base = apiBaseUrl || process.env.REACT_APP_API_URL || "http://localhost:4000";
    fetch(`${base}/api/play/session-infra?code=${encodeURIComponent(code)}`)
      .then(r => r.json())
      .then(data => {
        const door = data?.infraState?.stage3?.door;
        if (door) { setDoorState(door); if (door.unlocked) { setDoorComplete(true); setShowCutscene(true); } }
        if (data?.infraState?.stage3?.copy?.usbEjected === true) setUsbEjected(true);
        const srv = data?.infraState?.stage3?.server;
        if (srv) setServerState(srv);
        if (srv?.allUploaded) setServerComplete(true);
        const mon = data?.infraState?.stage3?.monitor;
        if (mon) { setMonitorState(mon); if (mon.unlocked) setPadUnlocked(true); }
      })
      .catch(console.error);
  }, [code, apiBaseUrl]);

  const handleServerAction = useCallback((action, payload) => {
    socketRef.current?.emit("stage3:server:action", { action, payload });
  }, []);
  const handleDoorSelect = useCallback(({ cardPos, isCorrect, confirm }) => {
    if (!confirm) return;
    socketRef.current?.emit("stage3:door:select", { cardPos, isCorrect });
  }, []);
  const handleDoorRelease = useCallback(() => { socketRef.current?.emit("stage3:door:press", { holding: false }); }, []);
  const handleMonitorRotate = useCallback((dir) => { socketRef.current?.emit("stage3:monitor:rotate", { dir }); }, []);
  const handleMonitorTest = useCallback(() => { socketRef.current?.emit("stage3:monitor:test"); }, []);

  const isFirstRenderRef = useRef(true);
  const hasFinalizedComputerUnlockRef = useRef(false);

  const finalizeComputerUnlock = useCallback(() => {
    if (hasFinalizedComputerUnlockRef.current) return;
    hasFinalizedComputerUnlockRef.current = true;
    setUnlockPhase("granted");
    unlockScenarioStep("s3.computer.unlocked");
    missionResolve({ key: "s3.computer.found" });
    missionUnlock({ key: "s3.computer.unlocked", text: "Ordinateur du PDG déverrouillé — recherche de preuves en cours." });
  }, [unlockScenarioStep, missionResolve, missionUnlock]);

  useEffect(() => {
    if (!allReady) {
      hasAutoOpenedUnlockedRef.current = false;
      hasFinalizedComputerUnlockRef.current = false;
      setUnlockPhase("idle"); isFirstRenderRef.current = true; return;
    }
    if (hasAutoOpenedUnlockedRef.current) return;
    hasAutoOpenedUnlockedRef.current = true;
    if (isFirstRenderRef.current) { isFirstRenderRef.current = false; finalizeComputerUnlock(); return; }
    isFirstRenderRef.current = false;
    setShowSyncModal(true); setUnlockPhase("charging");
    const t1 = setTimeout(() => setUnlockPhase("pulse"), 900);
    const t2 = setTimeout(() => { finalizeComputerUnlock(); }, 1500);
    onAllReadyRef.current?.();
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [allReady, finalizeComputerUnlock]);

  const setMySwitch = useCallback((switchId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("stage3:switch:set", { switchId });
  }, []);

  const integrity = useMemo(() => {
    let correct = 0;
    for (let v = 1; v <= N; v++) {
      if (selectionByVariant[String(v)] === SOLUTION_BY_VARIANT[v]) correct++;
    }
    return N > 0 ? correct / N : 0;
  }, [selectionByVariant, N, SOLUTION_BY_VARIANT]);

  const pickedCount = activeSelectionEntries.length;

  const displayBars = useMemo(() => {
    const base = 0.15 + integrity * 0.75;
    const bars = Array.from({ length: 12 }, (_, i) => Math.max(0.08, Math.min(1, base + Math.sin(i * 1.3) * 0.1)));
    if (unlockPhase === "charging") return bars.map((v, i) => Math.max(0.08, Math.min(1, v + 0.18 + (i % 3) * 0.04)));
    if (unlockPhase === "pulse") return bars.map((v, i) => Math.max(0.08, Math.min(1, v + 0.40 - (i % 4) * 0.03)));
    if (unlockPhase === "granted") return bars.map(() => 0.85);
    return bars;
  }, [integrity, unlockPhase]);

  useEffect(() => { if (hintSignal > 0) setShowHintModal(true); }, [hintSignal]);

  const hintCategories = useMemo(() => [
    {
      id: "terminal", title: "Terminal", subtitle: "Synchronisation", hintKeyPrefix: "s3:synchronisation",
      levels: {
        l1: (<><p className="mb-2">Chaque joueur ne contrôle qu'une partie de la synchronisation.</p><p className="text-xs text-gray-300">Une seule case est correcte par poste.</p></>),
        l2: (<p className="mb-2">Lorsqu'une zone est correcte, le moniteur de synchronisation le montre via l'intégrité.</p>),
        l3: (<p>Procédez poste par poste pour faire augmenter l'intégrité chacun votre tour.</p>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse — Synchronisation</p><div className="mt-2 space-y-1 text-xs text-gray-200 font-mono">{syncAnswerLines.map(({ variant, switchId }) => (<p key={variant}>J{variant} → <span className="text-emerald-300 font-bold">{switchId}</span></p>))}</div></>),
      },
    },
    {
      id: "session", title: "Ordinateur", subtitle: "Récupération des preuves", hintKeyPrefix: "s3:ordinateur",
      levels: {
        l1: (<p className="mb-2">Vous devez récupérer tous les fichiers de preuves — il y en a un par joueur.</p>),
        l2: (<p>Vous avez un timer serré — chaque joueur doit s'occuper d'une preuve simultanément.</p>),
        l3: (<p>Glissez chaque preuve ⭐ vers la Clé USB (E:) dans la barre latérale. Synchronisez-vous à voix haute.</p>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse — Récupération des preuves</p><div className="space-y-1 text-xs text-gray-200 mt-2">{proofAnswers.map(p => (<p key={p.id}><span className="text-emerald-300 font-bold">Preuve {p.id} → </span>{p.path}</p>))}</div></>),
      },
    },
    {
      id: "server", title: "Serveur", subtitle: "Upload des preuves", hintKeyPrefix: "s3:server",
      levels: {
        l1: (<p>Vous avez besoin d'avoir la clé USB en main pour accéder au serveur.</p>),
        l2: (<p>Les preuves doivent être transférées vers le dossier <span className="font-mono">/CONFERENCE</span>.</p>),
        l3: (<p>Les actions se valident par vote. Insérer la clé USB → copier chaque preuve → aller dans <span className="font-mono">/CONFERENCE</span> → coller.</p>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse — Serveur</p><div className="mt-2 space-y-1 text-xs text-gray-200 font-mono"><p>1. Insérer la clé USB</p><p>2. Copier chaque preuve</p><p>3. Aller dans <span className="text-emerald-300">/CONFERENCE</span></p><p>4. Coller chaque preuve</p></div></>),
      },
    },
    {
      id: "monitor", title: "Panneau de monitoring", subtitle: "Reconfiguration biométrique", hintKeyPrefix: "s3:monitor",
      levels: {
        l1: (<><p className="mb-2">Le panneau de monitoring permet de reconfigurer le pad biométrique de la porte.</p><p className="text-xs text-gray-300">Cherchez le menu "Porte — Biométrie".</p></>),
        l2: (<p>Pendant la phase de mémorisation, un tableau affiche les positions cibles en crans. Notez-les !</p>),
        l3: (<p>Chaque joueur contrôle un anneau de sa couleur. Coordonnez-vous à voix haute : annoncez votre cran cible.</p>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse — Monitoring</p><p className="mt-2 text-xs text-gray-200">Pendant la mémorisation, lisez le tableau (couleur → cran cible). Ensuite tournez votre anneau avec ◀ ▶ jusqu'au bon cran. Quand tout le monde est en position, le niveau est validé.</p></>),
      },
    },
    {
      id: "door", title: "Porte de sortie", subtitle: "Contrôle biométrique", hintKeyPrefix: "s3:door",
      levels: {
        l1: (<p>La porte affiche une longue séquence à mémoriser pendant quelques secondes avant qu'elle disparaisse.</p>),
        l2: (<p>Chaque joueur apparaît 3 fois dans la séquence. Prenez note de l'ordre — il sera masqué.</p>),
        l3: (<p>Si quelqu'un appuie dans le mauvais ordre, une nouvelle séquence est générée avec une nouvelle phase de mémorisation.</p>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse — Porte biométrique</p><p className="mt-1 text-xs text-gray-200">Mémorisez la séquence pendant les secondes d'affichage, puis reproduisez-la dans l'ordre exact. Chacun appuie sur son empreinte quand son tour vient selon l'ordre mémorisé.</p></>),
      },
    },
  ], [syncAnswerLines, proofAnswers]);

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square" style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>

        <div className="absolute left-4 top-4 z-40 flex items-center gap-2">
          <div className="rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200">
            Bureau du PDG — Accès synchronisé
          </div>
        </div>
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>

        <img src="/assets/s3-room.png" alt="Salle — Stage 3" className="absolute inset-0 h-full w-full object-contain" />

        {/* Hotspot Terminal */}
        <img src="/assets/stage3-hotspot-terminal.png" alt="Terminal"
          onClick={() => {
            setShowSyncModal(true);
            markInspectedOnce('stage3player:terminal');
            missionUnlock({ key: "s3.terminal.found", text: `${pseudo} a trouvé le terminal de synchronisation.` });
            if (allReady) setUnlockPhase("granted");
          }}
          className="stage3-hotspot-terminal absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.9)]"
          title="Terminal de synchronisation"
        />

        {/* Hotspot Ordinateur */}
        <img src="/assets/stage3-hotspot-locked.png"
          alt={allReady ? "Ordinateur du PDG (déverrouillé)" : "Ordinateur du PDG verrouillé"}
          onClick={() => {
            markInspectedOnce('stage3player:computer');
            if (!allReady) {
              missionUnlock({ key: "s3.computer.found", text: `${pseudo} a trouvé l'ordinateur du PDG — il est verrouillé.` });
              unlockScenarioStep("s3.computer.locked");
              setShowLockedModal(true);
            } else { setShowPdgModal(true); }
          }}
          className="stage3-hotspot-locked absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.9)]"
        />

        {/* Hotspot Serveur */}
        <img src="/assets/stage3-hotspot-server.png" alt="Serveur"
          onClick={() => {
            markInspectedOnce('stage3player:server');
            missionUnlock({ key: "s3.server.found", text: `${pseudo} a trouvé le rack serveur.` });
            setShowServerModal(true);
          }}
          className="stage3-hotspot-server absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.9)]"
        />

        {/* Hotspot Monitor */}
        <img src="/assets/stage3-hotspot-monitor.png" alt="Panneau de monitoring"
          onClick={() => {
            markInspectedOnce('stage3player:monitor');
            missionUnlock({ key: "s3.monitor.found", text: `${pseudo} a trouvé le panneau de monitoring.` });
            setShowMonitorModal(true);
            if (serverComplete) socketRef.current?.emit("stage3:monitor:init");
          }}
          className="stage3-hotspot-monitor absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.9)]"
          title="Panneau de monitoring"
        />

        {/* Hotspot Porte */}
        <img src="/assets/stage3-hotspot-door.png" alt="Porte de sortie"
          onClick={() => {
            markInspectedOnce('stage3player:door');
            missionUnlock({ key: "s3.door.found", text: `${pseudo} a trouvé la porte de sortie.` });
            if (doorComplete) { setShowCutscene(true); }
            else { setShowDoorModal(true); socketRef.current?.emit("stage3:door:init"); }
          }}
          className="stage3-hotspot-door absolute cursor-pointer transition-all duration-200 hover:brightness-150"
          title={doorComplete ? "Salle de conférence" : padUnlocked ? "Porte de sortie" : "Reconfiguration biométrique requise"}
        />
      </div>

      {/* MODALE 1 : Terminal synchro */}
      <ModalRegister open={showSyncModal}
        title={allReady ? "Terminal — Synchronisation effectuée" : "Terminal de synchronisation"}
        onClose={() => setShowSyncModal(false)} onHint={() => setShowHintModal(true)}>
        <div className="space-y-3">
          <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
            <div className="rounded-2xl border border-emerald-400/20 bg-black/60 p-4 relative overflow-hidden">
              <div className="pointer-events-none absolute -inset-10 opacity-60 blur-2xl"
                style={{ background: "radial-gradient(circle at 30% 20%, rgba(16,185,129,0.25), transparent 55%), radial-gradient(circle at 80% 70%, rgba(99,102,241,0.18), transparent 55%)" }} />

              {allReady ? (
                // ── ÉTAT SUCCÈS ──────────────────────────────────────────────────────────
                <div className="relative flex flex-col items-center gap-4 py-4">
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: "rgba(16,185,129,0.12)",
                    border: "2px solid rgba(16,185,129,0.6)",
                    boxShadow: "0 0 32px rgba(16,185,129,0.35)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 24,
                    animation: "sync-pulse 2s ease-in-out infinite",
                  }}>✓</div>
                  <div style={{
                    fontFamily: "ui-monospace,monospace",
                    fontSize: 11, fontWeight: 800, letterSpacing: "0.22em",
                    textTransform: "uppercase", color: "rgba(16,185,129,0.95)",
                    textShadow: "0 0 20px rgba(16,185,129,0.5)",
                    textAlign: "center",
                  }}>
                    Synchronisation terminée
                  </div>
                  <div style={{
                    fontFamily: "ui-sans-serif,system-ui",
                    fontSize: 13, color: "rgba(148,163,184,0.85)",
                    textAlign: "center", lineHeight: 1.6,
                  }}>
                    Accès à l'ordinateur obtenu.<br />
                    <span style={{ color: "rgba(16,185,129,0.7)", fontSize: 11, fontFamily: "ui-monospace,monospace" }}>
                      INTEGRITE :: 100%
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {Array.from({ length: 12 }, (_, i) => (
                      <div key={i} style={{
                        width: 4, borderRadius: 2,
                        background: "rgba(16,185,129,0.75)",
                        height: `${18 + Math.sin(i * 0.8) * 10}px`,
                        boxShadow: "0 0 6px rgba(16,185,129,0.4)",
                        animation: `sync-bar-${i % 3} 1.${i % 4}s ease-in-out infinite alternate`,
                      }} />
                    ))}
                  </div>
                  <style>{`
        @keyframes sync-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(16,185,129,0.3); }
          50% { box-shadow: 0 0 40px rgba(16,185,129,0.6); }
        }
        @keyframes sync-bar-0 { from { height: 8px; } to { height: 28px; } }
        @keyframes sync-bar-1 { from { height: 14px; } to { height: 22px; } }
        @keyframes sync-bar-2 { from { height: 10px; } to { height: 26px; } }
      `}</style>
                </div>
              ) : (
                // ── ÉTAT EN COURS ─────────────────────────────────────────────────────────
                <div className="relative">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-200/90">Moniteur de synchronisation</div>
                    <span className={["inline-flex items-center gap-2 rounded-full border px-2 py-1 text-[10px] tracking-[0.16em] uppercase",
                      pickedCount === N ? "border-emerald-400/50 text-emerald-200" : "border-white/15 text-slate-200"].join(" ")}>
                      <span className={["h-2 w-2 rounded-full", pickedCount === N ? "bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.8)]" : "bg-slate-400"].join(" ")} />
                      {pickedCount}/{N} en ligne
                    </span>
                  </div>
                  <div className="grid grid-cols-12 gap-1 mb-3">
                    {displayBars.map((h, i) => (
                      <div key={i} className="h-12 rounded-md bg-white/5 border border-white/10 relative overflow-hidden">
                        <div className="absolute bottom-0 left-0 right-0 rounded-md"
                          style={{ height: `${Math.round(h * 100)}%`, background: "linear-gradient(to top, rgba(16,185,129,0.75), rgba(99,102,241,0.35))" }} />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[11px] text-slate-300/90">Intégrité</div>
                    <div className="flex-1 h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                      <div className="h-full" style={{ width: `${Math.round(integrity * 100)}%`, background: "linear-gradient(90deg, rgba(16,185,129,0.75), rgba(99,102,241,0.55))" }} />
                    </div>
                    <div className="text-[11px] text-slate-200 tabular-nums">{Math.round(integrity * 100)}%</div>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
              {zones.map(zone => {
                const isMyZone = zone.variant === effectiveMyVariant;
                const selectedPad = selectionByVariant[String(zone.variant)];
                return (
                  <div key={zone.variant} className={["rounded-2xl border p-4", isMyZone ? "border-white/30 bg-white/5" : "border-white/10 bg-black/20"].join(" ")}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-semibold uppercase tracking-wider">{zone.pseudo}</div>
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: zone.color, boxShadow: `0 0 10px ${zone.color}` }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {zone.pads.map(padNum => {
                        const isSelected = selectedPad === padNum;
                        const isMyPad = isMyZone;
                        return (
                          <button key={padNum} disabled={!isMyPad || !connected}
                            onClick={() => { if (isMyPad) setMySwitch(isSelected ? null : padNum); }}
                            className={["aspect-square rounded-xl border transition-all duration-200 flex items-center justify-center text-sm font-bold",
                              isSelected ? "border-emerald-400/60 bg-emerald-500/20" : "border-white/10 bg-black/40",
                              isMyPad && !isSelected ? "hover:border-white/30 cursor-pointer" : "cursor-not-allowed opacity-60"].join(" ")}
                            style={isSelected ? { boxShadow: `0 0 20px ${zone.color}`, color: zone.color } : {}}>
                            {padNum}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </ModalRegister>

      {/* MODALE 2 : Ordinateur verrouillé */}
      <Modal open={showLockedModal} title="" onClose={() => setShowLockedModal(false)} onHint={() => setShowHintModal(true)}>
        <div className="relative overflow-hidden rounded-xl flex flex-col items-center justify-center p-8 min-h-[400px]"
          style={{ background: "radial-gradient(ellipse at 50% 30%, #2a0505 0%, #120000 50%, #080000 100%)", border: "1px solid rgba(239,68,68,0.25)" }}>
          <div className="relative flex items-center justify-center mb-4" style={{ width: 160, height: 160 }}>
            <svg width="160" height="160" viewBox="0 0 160 160" style={{ animation: "spin-slow 12s linear infinite", position: "absolute" }}>
              <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(239,68,68,0.35)" strokeWidth="1" strokeDasharray="8 6" />
            </svg>
            <svg width="56" height="64" viewBox="0 0 56 64" fill="none">
              <path d="M12 28V18C12 9.163 19.163 2 28 2C36.837 2 44 9.163 44 18V28" stroke="rgba(239,68,68,0.95)" strokeWidth="3.5" strokeLinecap="round" />
              <rect x="4" y="28" width="48" height="34" rx="5" fill="rgba(239,68,68,0.12)" stroke="rgba(239,68,68,0.95)" strokeWidth="2.5" />
              <circle cx="28" cy="43" r="5" fill="rgba(239,68,68,0.8)" />
            </svg>
          </div>
          <div className="px-6 py-2 rounded border-2 font-mono font-black tracking-[0.3em] uppercase text-lg mb-6"
            style={{ borderColor: "rgba(239,68,68,0.7)", color: "rgba(239,68,68,0.95)", background: "rgba(239,68,68,0.08)", textShadow: "0 0 15px rgba(239,68,68,0.8)" }}>
            ACCÈS REFUSÉ
          </div>
          <div className="w-full max-w-xs rounded border border-red-500/20 bg-black/40 px-4 py-3 font-mono text-xs space-y-1.5">
            <div className="flex gap-2 text-red-400/70"><span>›</span><span>ERR <span className="text-red-300/40">0x4F3A</span> — Auth failed</span></div>
            <div className="flex gap-2 text-red-400/50"><span>›</span><span>Requis : <span className="text-amber-400/80">sync multi-postes</span></span></div>
          </div>
          {allReady && (
            <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl"
              style={{ background: "radial-gradient(ellipse at 50% 30%, #001a05 0%, #000a00 60%)" }}>
              <div className="font-mono font-black tracking-[0.25em] uppercase text-xl mb-1" style={{ color: "rgba(16,185,129,0.95)" }}>ACCÈS ACCORDÉ</div>
              <div className="text-[10px] font-mono text-emerald-400/50 tracking-[0.2em] uppercase mb-6">Synchronisation validée</div>
              <button type="button" onClick={() => { setShowLockedModal(false); setShowPdgModal(true); }}
                className="px-6 py-2.5 rounded font-mono text-sm font-semibold tracking-[0.15em] uppercase transition-all"
                style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.5)", color: "#34d399" }}>
                ▶ Accéder à l'ordinateur
              </button>
            </div>
          )}
          <style>{`@keyframes spin-slow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </Modal>

      {/* MODALE 3 : Ordinateur PDG */}
      <div className="fixed inset-0 z-[40] flex items-center justify-center bg-black/90" style={{ display: showPdgModal ? "flex" : "none" }}>
        <div className="relative w-[95vw] h-[90vh] bg-slate-950 border border-white/20 rounded-lg overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900/90 border-b border-white/10">
            <div className="text-sm font-semibold text-white">Ordinateur du PDG — Accès autorisé</div>
            <div className="flex items-center gap-2">
              <HintButton onHint={() => setShowHintModal(true)} />
              <button onClick={() => setShowPdgModal(false)} className="w-6 h-6 rounded bg-red-500/20 hover:bg-red-500/40 border border-red-400/40 flex items-center justify-center text-red-200 text-xs transition">✕</button>
            </div>
          </div>
          <div className="h-[calc(100%-48px)]">
            {!allReady ? (
              <div className="flex items-center justify-center h-full"><p className="text-sm text-slate-200">Accès refusé. Synchronisation incomplète.</p></div>
            ) : (
              <PdgOS key={`pdg-${code}`} code={code} myVariant={effectiveMyVariant} playerCount={N} socketRef={socketRef} apiBaseUrl={apiBaseUrl} onAuthSuccess={() => { }} />
            )}
          </div>
        </div>
      </div>

      {/* MODALE 4 : Serveur */}
      <div className="fixed inset-0 z-[40] flex items-center justify-center bg-black/95"
        style={{ display: showServerModal ? "flex" : "none" }}
        onClick={() => setShowServerModal(false)}>
        <div className="relative rounded-xl overflow-hidden shadow-2xl"
          style={{ width: "780px", height: "580px", background: "#050d05", border: "2px solid #1a4a1a" }}
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-2" style={{ background: "#0a1a0a", borderBottom: "1px solid #1a3a1a" }}>
            <div className="flex items-center gap-3">
              <div className="flex gap-1">{[...Array(3)].map((_, i) => (<div key={i} className="w-2 h-2 rounded-full animate-pulse" style={{ background: i === 0 ? "#00ff44" : i === 1 ? "#ffaa00" : "#ff4444", animationDelay: `${i * 0.3}s` }} />))}</div>
              <span style={{ color: "#00aa44", fontSize: "11px", letterSpacing: "0.2em" }}>RACK-SERVER :: TRANSFERT SÉCURISÉ</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <HintButton onHint={() => setShowHintModal(true)} />
              <button onClick={() => setShowServerModal(false)} className="w-6 h-6 rounded flex items-center justify-center text-xs" style={{ background: "#1a0000", border: "1px solid #440000", color: "#ff4444" }}>✕</button>
            </div>
          </div>
          <div className="p-4 h-[calc(100%-44px)]">
            <ServerTerminal usbEjected={usbEjected} myVariant={effectiveMyVariant} playerCount={N}
              serverState={serverState} voteState={voteState} onAction={handleServerAction}
              onDebugForceUpload={() => socketRef.current?.emit("stage3:server:debug:forceUpload")} />
          </div>
        </div>
      </div>

      {/* MODALE 5 : Monitor Dashboard */}
      {showMonitorModal && (
        <div className="fixed inset-0 z-[40] flex items-center justify-center bg-black/90"
          onClick={() => setShowMonitorModal(false)}>
          <div className="relative rounded-xl overflow-hidden shadow-2xl"
            style={{ width: "92vw", height: "88vh", maxWidth: "1100px", maxHeight: "780px" }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowMonitorModal(false)}
              style={{
                position: "absolute", top: 8, right: 8, zIndex: 10,
                width: 24, height: 24, borderRadius: 4, border: "1px solid #2a1a3a",
                background: "#0a060f", color: "#4a3a5c", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px",
              }}>✕</button>
            <MonitorDashboard
              serverComplete={serverComplete}
              myVariant={effectiveMyVariant}
              playerCount={N}
              lockState={monitorState}
              pseudoByVariant={pseudoByVariant}
              onRotate={handleMonitorRotate}
              onTest={handleMonitorTest}
              socketRef={socketRef}
            />
          </div>
        </div>
      )}

      {/* MODALE 6 : Porte biométrique — AGRANDIE */}
      {showDoorModal && (
        <div className="fixed inset-0 z-[40] flex items-center justify-center bg-black/95"
          onClick={() => setShowDoorModal(false)}>
          <div className="relative rounded-xl overflow-hidden shadow-2xl"
            style={{ width: "min(96vw, 1140px)", height: "min(94vh, 800px)", background: "#04080c", border: "2px solid #003344" }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2" style={{ background: "#060c12", borderBottom: "1px solid #002233" }}>
              <div className="flex items-center gap-2">
                <span style={{ color: "#0088aa", fontSize: "11px", letterSpacing: "0.2em" }}>PORTE SÉCURISÉE — SALLE DE CONFÉRENCE</span>
                {!padUnlocked && <span style={{ color: "#fbbf2480", fontSize: "9px" }}>— PAD NON RECONFIGURÉ</span>}
                {padUnlocked && <span style={{ color: "#34d39980", fontSize: "9px" }}>— PAD ACTIF</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <HintButton onHint={() => setShowHintModal(true)} />
                <button onClick={() => setShowDoorModal(false)} style={{ background: "#0a0000", border: "1px solid #220000", color: "#ff4444", width: 24, height: 24, borderRadius: 4 }}>✕</button>
              </div>
            </div>
            <div style={{ height: "calc(100% - 44px)" }}>
              <DoorPad
                myVariant={effectiveMyVariant}
                playerCount={N}
                doorState={doorState}
                onSelectFragment={handleDoorSelect}
                onRelease={handleDoorRelease}
                onStart={() => socketRef.current?.emit("stage3:door:start")}
                serverComplete={serverComplete && padUnlocked}
                onValidationComplete={() => {
                  setShowDoorModal(false);  // on ferme la modale APRÈS l'animation
                  setShowCutscene(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showCutscene && (
        <CutsceneSlideshow isCanAdvance={isCanAdvance} canAdvance={onNavigateToScoreboard} onSkip={onNavigateToScoreboard} />
      )}

      <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)}
        title="Indices — Bureau du PDG"
        markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
        usedStorageKey={`hints_used:${code}:s3:p${playerId}`} />
    </div>
  );
}